/**
 * Civil Disclosure Slice Card Component
 * 
 * Reusable card to display slice status and navigation
 */

import React from 'react';
import { Card } from './Card';
import { Badge } from './Badge';
import { MetricTile } from './MetricTile';

interface CivilDisclosureSliceCardProps {
  sliceDefinition: any;
  bindingStatus: any;
  onNavigateToModule?: (moduleId: string) => void;
  onNavigateToArtifact?: (artifactPath: string) => void;
  onVerifyBinding?: () => void;
}

export const CivilDisclosureSliceCard: React.FC<CivilDisclosureSliceCardProps> = ({
  sliceDefinition,
  bindingStatus,
  onNavigateToModule,
  onNavigateToArtifact,
  onVerifyBinding,
}) => {
  const getBindingStatusBadge = () => {
    if (!bindingStatus) {
      return <Badge variant="neutral">Unknown</Badge>;
    }
    
    switch (bindingStatus.kind) {
      case 'resolved_authoritative':
        return <Badge variant="success">Authoritative Binding</Badge>;
      case 'broken':
        return <Badge variant="danger">Binding Error</Badge>;
      case 'absent':
        return <Badge variant="neutral">No Binding</Badge>;
      default:
        return <Badge variant="neutral">Unknown</Badge>;
    }
  };

  return (
    <Card className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">
          Civil Disclosure Slice
        </h3>
        {getBindingStatusBadge()}
      </div>
      
      <p className="text-gray-300 mb-4">
        {sliceDefinition?.description || 'Governed civil evidence and disclosure workflow'}
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <MetricTile
          label="Authoritative Artifact"
          value="Case Package"
          description={sliceDefinition?.authoritativeArtifact?.path || 'artifacts/email-case-package/case-package.json'}
        />
        <MetricTile
          label="Policy Pack"
          value={bindingStatus?.linkedPolicyPack?.packId || 'Not bound'}
          description={bindingStatus?.linkedPolicyPack?.version || 'No version'}
        />
      </div>
      
      <div className="border-t border-white/10 pt-4">
        <h4 className="text-sm font-medium text-gray-400 mb-2">Module Chain</h4>
        <div className="space-y-2">
          {sliceDefinition?.moduleChain && Object.entries(sliceDefinition.moduleChain).map(([key, module]: [string, any]) => (
            <div key={key} className="flex items-center justify-between p-2 bg-white/5 rounded">
              <div>
                <span className="text-sm text-white capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </span>
                <span className="text-xs text-gray-400 ml-2">
                  {module.moduleId}@{module.moduleVersion}
                </span>
              </div>
              {onNavigateToModule && (
                <button
                  onClick={() => onNavigateToModule(module.moduleId)}
                  className="text-xs text-[#00d4aa] hover:text-[#00a8cc] transition-colors"
                >
                  View
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
      
      <div className="flex gap-2 mt-4">
        {onNavigateToArtifact && (
          <button
            onClick={() => onNavigateToArtifact(sliceDefinition?.authoritativeArtifact?.path)}
            className="px-3 py-1 text-sm bg-[#00d4aa]/20 text-[#00d4aa] border border-[#00d4aa]/50 rounded hover:bg-[#00d4aa]/30 transition-colors"
          >
            View Artifact
          </button>
        )}
        {onVerifyBinding && (
          <button
            onClick={onVerifyBinding}
            className="px-3 py-1 text-sm bg-white/10 text-white border border-white/20 rounded hover:bg-white/20 transition-colors"
          >
            Verify Binding
          </button>
        )}
      </div>
    </Card>
  );
};
